import type { ExportResolution, SafeRect, StaticExportFormat, StaticExportMode } from '../types';
import { createCanvas } from './loadImage';
import type { VideoExportArtifact } from './videoExportStorage';
import { computeSmartCrop, type FrameRect } from './staticFraming';
import type { StaticFraming } from './staticFraming';
import type { DynamicZoomBackgroundMotion, DynamicZoomViewport } from './dynamicZoom';
import { drawDynamicZoomFrame } from './dynamicZoomBackground';
import { isCanvasMemoryScopeActive } from './canvasMemory';

const FORMAT_TO_SIZE: Record<ExportResolution, Record<StaticExportFormat, { width: number; height: number }>> = {
  '4k': {
    '1:1': { width: 3840, height: 3840 },
    '1:1z': { width: 2560, height: 2560 },
    '16:9': { width: 3840, height: 2160 },
    '9:16': { width: 2160, height: 3840 },
    '2:3': { width: 2560, height: 3840 }
  },
  '2k': {
    '1:1': { width: 1920, height: 1920 },
    '1:1z': { width: 1920, height: 1920 },
    '16:9': { width: 1920, height: 1080 },
    '9:16': { width: 1080, height: 1920 },
    '2:3': { width: 1280, height: 1920 }
  }
};

export function getStaticExportSize(format: StaticExportFormat, resolution: ExportResolution): { width: number; height: number } {
  return FORMAT_TO_SIZE[resolution][format];
}

export function renderStaticExport(
  masterCanvas: HTMLCanvasElement,
  format: StaticExportFormat,
  resolution: ExportResolution,
  mode: StaticExportMode,
  safeRect: SafeRect,
  options: {
    fitBackground?: string;
    preserveAlpha?: boolean;
    matteColor?: string;
    sourceRect?: FrameRect;
    dynamicCamera?: { viewport: DynamicZoomViewport; framing: StaticFraming; backgroundMotion?: DynamicZoomBackgroundMotion };
    /** Export-owned surface; other callers keep the fresh-canvas behavior. */
    outputCanvas?: HTMLCanvasElement;
  } = {}
): HTMLCanvasElement {
  const { width, height } = getStaticExportSize(format, resolution);
  const output = options.outputCanvas ?? createCanvas(width, height);
  if (output.width !== width) output.width = width;
  if (output.height !== height) output.height = height;
  const outputCtx = output.getContext('2d');
  if (!outputCtx) {
    throw new Error('2D context is not available for export canvas');
  }
  // A reused surface may contain the previous camera view, including pixels
  // outside the new transparent silhouette or fit rectangle.
  if (options.outputCanvas) outputCtx.clearRect(0, 0, width, height);
  const fitBackground = options.fitBackground ?? '#111111';
  const preserveAlpha = options.preserveAlpha === true;
  const matteColor = options.matteColor;

  if (matteColor) {
    outputCtx.fillStyle = matteColor;
    outputCtx.fillRect(0, 0, width, height);
  }

  if (options.dynamicCamera) {
    const camera = options.dynamicCamera;
    drawDynamicZoomFrame(outputCtx, masterCanvas, camera.viewport, camera.framing, camera.backgroundMotion);
    if (preserveAlpha && !matteColor) normalizeTransparentPixels(outputCtx, width, height);
    return output;
  }

  if (options.sourceRect) {
    const crop = options.sourceRect;
    outputCtx.drawImage(masterCanvas, crop.x, crop.y, crop.width, crop.height, 0, 0, width, height);
    if (preserveAlpha && !matteColor) normalizeTransparentPixels(outputCtx, width, height);
    return output;
  }

  if (mode === 'zoom') {
    // master was rendered at outputWidth * 1.5 — center-crop to output size
    const srcX = (masterCanvas.width - width) / 2;
    const srcY = (masterCanvas.height - height) / 2;
    outputCtx.drawImage(masterCanvas, srcX, srcY, width, height, 0, 0, width, height);
    if (preserveAlpha && !matteColor) {
      normalizeTransparentPixels(outputCtx, width, height);
    }
    return output;
  }

  if (mode === 'fit') {
    if (!preserveAlpha && !matteColor) {
      outputCtx.fillStyle = fitBackground;
      outputCtx.fillRect(0, 0, width, height);
    }

    const scale = Math.min(width / masterCanvas.width, height / masterCanvas.height);
    const drawWidth = masterCanvas.width * scale;
    const drawHeight = masterCanvas.height * scale;
    const dx = (width - drawWidth) / 2;
    const dy = (height - drawHeight) / 2;

    outputCtx.drawImage(masterCanvas, dx, dy, drawWidth, drawHeight);
    if (preserveAlpha && !matteColor) {
      normalizeTransparentPixels(outputCtx, width, height);
    }
    return output;
  }

  const crop = computeSmartCrop(masterCanvas.width, masterCanvas.height, width / height, safeRect);
  outputCtx.drawImage(
    masterCanvas,
    crop.x,
    crop.y,
    crop.width,
    crop.height,
    0,
    0,
    width,
    height
  );

  if (preserveAlpha && !matteColor) {
    normalizeTransparentPixels(outputCtx, width, height);
  }

  return output;
}

export const getExportSize = getStaticExportSize;
export const renderExport = renderStaticExport;

export function downloadBlobFile(blob: Blob, fileName: string): void {
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = fileName;
  link.style.display = 'none';
  document.body.appendChild(link);
  link.click();
  link.remove();
  window.setTimeout(() => URL.revokeObjectURL(url), 60_000);
}

export function downloadVideoExportArtifact(
  artifact: VideoExportArtifact,
  fileName: string
): void {
  if (artifact.storageKind === 'direct-file') {
    return;
  }
  const url = URL.createObjectURL(artifact.blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = fileName;
  link.style.display = 'none';
  document.body.appendChild(link);
  link.click();
  link.remove();
  window.setTimeout(() => {
    URL.revokeObjectURL(url);
    void artifact.cleanup();
  }, 5 * 60_000);
}

export async function downloadCanvasPng(canvas: HTMLCanvasElement, fileName: string): Promise<void> {
  const blob = await new Promise<Blob>((resolve, reject) => {
    canvas.toBlob((nextBlob) => {
      if (!nextBlob) {
        reject(new Error('Failed to encode PNG blob'));
        return;
      }
      resolve(nextBlob);
    }, 'image/png');
  });

  downloadBlobFile(blob, fileName);
}

function normalizeTransparentPixels(ctx: CanvasRenderingContext2D, width: number, height: number): void {
  const rowLimit = isCanvasMemoryScopeActive() ? 128 : height;
  for (let y = 0; y < height; y += rowLimit) {
    const imageData = ctx.getImageData(0, y, width, Math.min(rowLimit, height - y));
    const pixels = imageData.data;
    for (let index = 0; index < pixels.length; index += 4) {
      if (pixels[index + 3] === 0) {
        pixels[index] = 0;
        pixels[index + 1] = 0;
        pixels[index + 2] = 0;
      }
    }
    ctx.putImageData(imageData, 0, y);
  }
}
